<template>
	<v-autocomplete
		v-model="internalValue"
		:items="filteredItems"
		item-text="text"
		item-value="value"
		:label="label"
		:outlined="outlined"
		:dense="dense"
		:hide-details="hideDetails"
		:clearable="clearable"
		:disabled="disabled"
		:error-messages="errorMessages"
		:name="name"
		no-filter
		:search-input.sync="searchInput"
		@change="handleChange"
		@input="handleInput"
	></v-autocomplete>
</template>

<script>
import { mapState } from 'vuex';
import { toLocaleCapitalize } from '@/utils/helpers';

export default {
	name: 'TurkishLocationPicker',
	props: {
		value: {
			type: String,
			default: null,
		},
		type: {
			type: String,
			required: true,
			validator: value => ['city', 'county'].includes(value),
		},
		selectedCity: {
			type: String,
			default: null,
		},
		label: {
			type: String,
			required: true,
		},
		outlined: {
			type: Boolean,
			default: true,
		},
		dense: {
			type: Boolean,
			default: true,
		},
		hideDetails: {
			type: Boolean,
			default: true,
		},
		clearable: {
			type: Boolean,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		errorMessages: {
			type: Array,
			default: () => [],
		},
		name: {
			type: String,
			default: '',
		},
	},
	computed: {
		...mapState({
			turkeyLocations: state => state.geolocations.turkeyLocations || [],
		}),
		internalValue: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit('input', val);
			},
		},
		baseItems() {
			if (this.type === 'city') {
				return this.turkeyLocations.map(city => ({
					...city,
					text: city?.name ? toLocaleCapitalize(city.name, 'tr') : city?.name || '',
					value: city?.name || '',
				})).filter(city => city.value);
			} else if (this.type === 'county') {
				if (!this.selectedCity || !this.turkeyLocations?.length) {
					return [];
				}
				const selectedCityData = this.turkeyLocations.find(city => city?.name === this.selectedCity);
				if (!selectedCityData || !selectedCityData.counties || !Array.isArray(selectedCityData.counties)) {
					return [];
				}
				return selectedCityData.counties.map(county => ({
					text: county ? toLocaleCapitalize(county, 'tr') : county,
					value: county,
				})).filter(county => county.value);
			}
			return [];
		},
		filteredItems() {
			if (!this.searchInput) {
				return this.baseItems;
			}
			const normalizedQuery = this.normalizeturkish(this.searchInput);
			
			return this.baseItems.filter(item => {
				const normalizedText = this.normalizeturkish(item.text || '');
				const normalizedValue = this.normalizeturkish(item.value || '');
				return normalizedText.includes(normalizedQuery) || normalizedValue.includes(normalizedQuery);
			});
		},
	},
	data() {
		return {
			searchInput: '',
		};
	},
	methods: {
		normalizeturkish(str) {
			if (!str) return '';
			return str
				.replace(/İ/g, 'i')
				.replace(/I/g, 'ı')
				.replace(/Ğ/g, 'g')
				.replace(/Ü/g, 'u')
				.replace(/Ş/g, 's')
				.replace(/Ö/g, 'o')
				.replace(/Ç/g, 'c')
				.toLowerCase()
				.replace(/ğ/g, 'g')
				.replace(/ü/g, 'u')
				.replace(/ş/g, 's')
				.replace(/ı/g, 'i')
				.replace(/ö/g, 'o')
				.replace(/ç/g, 'c');
		},
		handleChange(value) {
			this.searchInput = '';
			this.$emit('change', value);
		},
		handleInput(value) {
			this.$emit('input', value);
		},
	},
	watch: {
		value(newValue) {
			if (newValue && this.baseItems.length > 0) {
				const normalizedInputValue = this.normalizeturkish(newValue);
				const matchingItem = this.baseItems.find(item => {
					const normalizedItemValue = this.normalizeturkish(item.value || '');
					return normalizedItemValue === normalizedInputValue;
				});
				
				if (matchingItem && matchingItem.value !== newValue) {
					this.$nextTick(() => {
						this.$emit('input', matchingItem.value);
					});
				}
			}
		},
		baseItems() {
			if (this.value && this.baseItems.length > 0) {
				const normalizedInputValue = this.normalizeturkish(this.value);
				const matchingItem = this.baseItems.find(item => {
					const normalizedItemValue = this.normalizeturkish(item.value || '');
					return normalizedItemValue === normalizedInputValue;
				});
				
				if (matchingItem && matchingItem.value !== this.value) {
					this.$nextTick(() => {
						this.$emit('input', matchingItem.value);
					});
				}
			}
		},
	},
	async mounted() {
		await this.$store.dispatch('geolocations/getTurkeyLocations', { updateStore: true });
	},
};
</script> 